import React, { useState, useEffect, useCallback } from 'react'
import { DataTable as PrimereactDataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import Toolbar from '../toolbar/Toolbar'
import { api } from '../../../auth'
import {
    DataTableColumn,
    DataTableSelect,
    DataTablesProps,
    ToolbarConfigButton,
} from './DataTables.d'
import { handleAxiosErrors } from '../forms/errors/Helper'
import MobileRowCard, { MOBILE_CARD_FIELD } from './MobileRowCard'
import { Alert } from 'react-bootstrap'
import RenderFieldsErrors from '../forms/errors/RenderFieldsErrors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faList } from '@fortawesome/free-solid-svg-icons'

/** Filter state compatible with PrimeReact DataTableFilterMeta (value is required per key) */
type TableFilters = Record<string, { value: unknown; matchMode?: string }>

type LazyState = {
    first: number
    rows: number
    page?: number
    sortField?: string | null
    sortOrder?: 1 | 0 | -1 | null
    sortMode?: 'single' | 'multiple'
    filters?: TableFilters
}

const DataTable: React.FC<DataTablesProps> = ({
    config,
    toolbarConfig,
    columns = [],
    globalFilterFields = [],
    refreshTable = false,
    onRefreshed,
    onTableData,
}) => {
    const [loading, setLoading] = useState(false)
    const [refreshed, setRefreshed] = useState(false)
    const [totalRecords, setTotalRecords] = useState(0)
    const [totalUnfiltered, setTotalUnfiltered] = useState<number | null>(null)
    const [records, setRecords] = useState<Record<string, unknown>[] | null>(null)
    const [selectAll, setSelectAll] = useState(false)
    const [filters, setFilters] = useState<TableFilters>(config.filters as TableFilters)
    const [selectedRecords, setSelectedRecords] = useState<Record<string, unknown>[] | null>(null)
    const [globalFilterValue, setGlobalFilterValue] = useState('')
    const [lazyState, setlazyState] = useState<LazyState>(config as LazyState)
    const [visibleColumns, setVisibleColumns] = useState(
        columns.filter((col) => col.isVisible !== false)
    )

    const [errMsg, setErrMsg] = useState('')
    const [errorFields, setErrorFields] = useState<any>({})

    // const [selectedColumns, setSelectedColumns] = useState(columns);

    const axiosPrivate = api

    const triggerOnTableData = useCallback(
        (data: unknown) => {
            if (onTableData) {
                onTableData(data)
            }
        },
        [onTableData]
    )

    const loadLazyData = useCallback(async () => {
        setLoading(true)
        setErrMsg('')
        setErrorFields({})

        axiosPrivate
            .post(process.env.REACT_APP_API_ROOT_URL + config.dataRoute, {
                page: (lazyState.page || 0) + 1,
                itemsPerPage: lazyState.rows,
                sortBy: lazyState.sortField,
                sortDesc: (lazyState.sortOrder === -1).toString(),
                filters: lazyState.filters,
            })
            .then((res) => res.data)
            .then((res) => {
                if (res.success) {
                    setTotalRecords(res.data.totalRecords)
                    setRecords(res.data.records)
                    setLoading(false)
                    triggerOnTableData(res.data)
                    if (refreshTable) {
                        onRefreshed?.()
                    }

                    const hasFilters =
                        lazyState.filters &&
                        Object.values(lazyState.filters ?? {}).some(
                            (f: { value?: unknown } | null | undefined) =>
                                f &&
                                f.value !== undefined &&
                                f.value !== null &&
                                String(f.value).trim() !== ''
                        )
                    if (hasFilters) {
                        axiosPrivate
                            .post(process.env.REACT_APP_API_ROOT_URL + config.dataRoute, {
                                page: 1,
                                itemsPerPage: 1,
                                sortBy: lazyState.sortField,
                                sortDesc: (lazyState.sortOrder === -1).toString(),
                                filters: {},
                            })
                            .then((r) => r.data)
                            .then((r) => {
                                if (r.success && r.data.totalRecords != null) {
                                    setTotalUnfiltered(r.data.totalRecords)
                                }
                            })
                            .catch(() => setTotalUnfiltered(null))
                    } else {
                        setTotalUnfiltered(null)
                    }
                }
            })
            .catch((e: unknown) => {
                type HandleAxiosErrorsFn = (
                    error: unknown,
                    setErrMsg: React.Dispatch<React.SetStateAction<string>>,
                    setErrorFields?: React.Dispatch<React.SetStateAction<unknown>>
                ) => void
                ;(handleAxiosErrors as HandleAxiosErrorsFn)(e, setErrMsg, setErrorFields)
            })
            .finally(() => {
                setLoading(false)
            })
    }, [triggerOnTableData, axiosPrivate, config.dataRoute, lazyState, refreshTable, onRefreshed])

    useEffect(() => {
        setRefreshed(false)
    }, [config.dataRoute, refreshTable, lazyState])

    useEffect(() => {
        if (!refreshed) {
            loadLazyData()
            setRefreshed(true)
        }
    }, [refreshed, loadLazyData])

    const onPage = (event: { first: number; rows: number; [key: string]: unknown }) => {
        setlazyState((prev) => ({ ...prev, first: event.first, rows: event.rows }))
    }

    const onSort = (event: { sortField?: string | null; sortOrder?: 1 | 0 | -1 | null; [key: string]: unknown }) => {
        setlazyState((prev) => ({ ...prev, sortField: event.sortField ?? undefined, sortOrder: event.sortOrder ?? undefined }))
    }

    const onFilter = (event: { first?: number; filters?: TableFilters; [key: string]: unknown }) => {
        setlazyState((prev) => ({ ...prev, ...event, first: 0 }))
    }

    const onSelectionChange = (event: { value: Record<string, unknown>[] }) => {
        const value = event.value ?? []
        setSelectedRecords(value)
        setSelectAll(value.length === totalRecords)
    }

    const onSelectAllChange = (event: { checked: boolean }) => {
        const newSelectAll = event.checked

        if (newSelectAll) {
            // RecordService.getRecords({}).then((data) => {
            //     setSelectAll(true)
            //     // setSelectedRecords(data.records)
            // })
        } else {
            setSelectAll(false)
            setSelectedRecords([])
        }
    }

    const onColumnToggle = (event: { value: string }) => {
        const visibleColumnsTest = columns.filter((obj) => obj.field !== event.value)
        let orderedSelectedColumns = columns.filter(
            (col: DataTableColumn | DataTableSelect) =>
                visibleColumnsTest.some((sCol) => sCol.field === col.field)
        )

        setVisibleColumns(orderedSelectedColumns)
    }

    const clearFilters = () => {
        initFilters()
        setTotalUnfiltered(null)
    }

    const onGlobalFilterChange = (value: string) => {
        const _filters: TableFilters = { ...filters }
        if (_filters['global']) _filters['global'].value = value
        setFilters(_filters)
        setlazyState((prevState) => {
            return {
                ...prevState,
                filters: _filters,
            }
        })
        setGlobalFilterValue(value)
    }

    const initFilters = () => {
        setFilters(config.filters as TableFilters)
        setGlobalFilterValue('')
    }

    const hasActiveFilters = () => {
        if (globalFilterValue && String(globalFilterValue).trim() !== '') return true
        const f = lazyState.filters ?? {}
        return Object.keys(f).some(
            (key) => {
                const filter = f[key] as { value?: unknown } | undefined
                return (
                    filter &&
                    filter.value !== undefined &&
                    filter.value !== null &&
                    String(filter.value).trim() !== ''
                )
            }
        )
    }

    const firstDisplay = totalRecords === 0 ? 0 : lazyState.first + 1
    const lastDisplay =
        totalRecords === 0 ? 0 : Math.min(lazyState.first + lazyState.rows, totalRecords)

    const resultsSummaryText =
        totalRecords === 0
            ? 'No results'
            : totalUnfiltered != null && totalUnfiltered !== totalRecords
              ? `Showing ${firstDisplay}–${lastDisplay} of ${totalRecords} filtered result${totalRecords === 1 ? '' : 's'} (from ${totalUnfiltered} total)`
              : `Showing ${firstDisplay}–${lastDisplay} of ${totalRecords} result${totalRecords === 1 ? '' : 's'}`

    const renderHeader = () => {
        let toolbarButtons: ToolbarConfigButton[] = [
            {
                type: 'toggle-columns',
                label: 'Toggle Columns',
                icon: 'fa-solid fa-table-columns',
                external: false,
                columnsOptions: {
                    columns,
                    visibleColumns,
                    onColumnToggle,
                    setVisibleColumns,
                },
            },
        ]

        if (toolbarConfig && toolbarConfig.includeExportBtn) {
            toolbarButtons.push({
                type: 'export',
                label: 'Export Options',
                icon: 'fa-solid fa-file-export',
                exportTypes: ['xlsx', 'csv', 'pdf'],
            })
        }
        const resultsSummaryBlock: React.ReactNode = (
            <>
                <FontAwesomeIcon icon={faList} className="text-primary opacity-75" />
                <span className="fw-medium">{resultsSummaryText}</span>
            </>
        )
        return (
            <div className="w-100">
                <Toolbar
                    globalFilterValue={globalFilterValue}
                    onGlobalFilterChange={onGlobalFilterChange}
                    clearFilters={clearFilters}
                    config={{
                        includeSearchBox: true,
                        buttons: toolbarButtons,
                    }}
                    leftContent={resultsSummaryBlock}
                    hasActiveFilters={hasActiveFilters()}
                />
            </div>
        )
    }

    const header = renderHeader()

    return (
        <>
            {errMsg && (
                <Alert show={true} variant="danger">
                    <Alert.Heading className="d-inline-block me-2">
                        Error!
                    </Alert.Heading>
                    <RenderFieldsErrors
                        errorFields={errorFields}
                        errMsg={errMsg}
                    />
                </Alert>
            )}
            <div className="w-100 overflow-visible">
                <PrimereactDataTable
                    value={records ?? []}
                    lazy
                    filterDisplay={config.filterDisplay}
                    dataKey="id"
                    paginator
                    size="small"
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="{first}–{last} of {totalRecords} results"
                    first={lazyState.first}
                    rows={lazyState.rows}
                    totalRecords={totalRecords}
                    onPage={onPage as (e: { first: number; rows: number }) => void}
                    onSort={onSort as (e: { sortField?: string; sortOrder?: 1 | 0 | -1 | null }) => void}
                    sortField={lazyState.sortField ?? undefined}
                    sortOrder={(lazyState.sortOrder ?? 1) as 1 | 0 | -1}
                    sortMode={lazyState.sortMode || 'single'}
                    onFilter={(e: unknown) => onFilter(e as { first?: number; filters?: TableFilters })}
                    filters={lazyState.filters as React.ComponentProps<typeof PrimereactDataTable>['filters']}
                    loading={loading}
                    tableStyle={{ width: '100%' }}
                    selectionMode="multiple"
                    selection={selectedRecords ?? []}
                    onSelectionChange={onSelectionChange}
                    selectAll={selectAll}
                    onSelectAllChange={onSelectAllChange}
                    globalFilterFields={globalFilterFields}
                    header={header as React.ReactNode}
                    emptyMessage={
                        <div className="p-3 text-center">No records found.</div>
                    }
                    rowHover
                    className="mb-4"
                >
                {/* <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} /> */}
                {visibleColumns.map((col: DataTableColumn | DataTableSelect) => {
                    const isActions =
                        'header' in col && String(col.header).toLowerCase() === 'actions'
                    const isDate = 'dataType' in col && col.dataType === 'date'
                    const widthClass = isActions
                        ? 'datatable-col-actions'
                        : isDate
                          ? 'datatable-col-date'
                          : 'datatable-col-flex'
                    const fixedCenterClass =
                        'fixedCenter' in col && col.fixedCenter ? 'datatable-col-fixed-center' : ''
                    const headerClass = [col.headerClassName, widthClass, fixedCenterClass].filter(Boolean).join(' ')
                    const bodyClass = `datatable-desktop-cell ${col.bodyClassName ?? ''} ${widthClass} ${fixedCenterClass}`.trim()
                    return (
                        <Column
                            key={col.field}
                            {...(col as React.ComponentProps<typeof Column>)}
                            headerClassName={headerClass}
                            bodyClassName={bodyClass}
                        />
                    )
                })}
                <Column
                    key={MOBILE_CARD_FIELD}
                    field={MOBILE_CARD_FIELD}
                    header=""
                    headerClassName="datatable-mobile-card-header"
                    body={(rowData: Record<string, unknown>) => (
                        <MobileRowCard
                            rowData={rowData}
                            columns={visibleColumns}
                        />
                    )}
                    bodyClassName="datatable-mobile-card-cell"
                    sortable={false}
                    style={{ width: '100%' }}
                />
                </PrimereactDataTable>
            </div>
        </>
    )
}

export default DataTable
